import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith, switchMap } from 'rxjs';
import { ProductsService } from '../../services/products.service';
import { Product } from '../../models/product';

type DetailState =
  | { status: 'loading'; product?: undefined }
  | { status: 'ready'; product: Product | undefined };

@Component({
  selector: 'app-product-detail',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './product-detail.html',
  styleUrl: './product-detail.scss',
})
export class ProductDetail {
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);
  private readonly productsService = inject(ProductsService);

  readonly state = toSignal(
    this.route.paramMap.pipe(
      map((params) => params.get('slug') ?? ''),
      switchMap((slug) =>
        this.productsService.getBySlug(slug).pipe(
          map((product): DetailState => ({ status: 'ready', product })),
          startWith({ status: 'loading' } as DetailState)
        )
      )
    ),
    { initialValue: { status: 'loading' } as DetailState }
  );

  readonly product = computed(() =>
    this.state().status === 'ready' ? this.state().product : undefined
  );

  readonly related = toSignal(
    this.route.paramMap.pipe(
      map((params) => params.get('slug') ?? ''),
      switchMap((slug) =>
        this.productsService
          .getAll()
          .pipe(map((products) => products.filter((p) => p.slug !== slug).slice(0, 2)))
      )
    ),
    { initialValue: [] as Product[] }
  );

  readonly activeMedia = signal(0);
  readonly media = computed(() => this.product()?.media ?? []);

  constructor() {
    effect(() => {
      const current = this.state();
      if (current.status !== 'ready') return;

      this.activeMedia.set(0);
      const p = current.product;
      this.title.setTitle(
        p ? `${p.title} — Trieon Technologies` : 'Product not found — Trieon Technologies'
      );
    });
  }

  prevMedia() {
    const total = this.media().length;
    if (total < 2) return;
    this.activeMedia.update((i) => (i - 1 + total) % total);
  }

  nextMedia() {
    const total = this.media().length;
    if (total < 2) return;
    this.activeMedia.update((i) => (i + 1) % total);
  }

  goToMedia(index: number) {
    this.activeMedia.set(index);
  }
}
